import { map } from 'lodash'
import { useCallback, useMemo } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import {
  type useUserPrompts_query$data,
  type useUserPrompts_query$key
} from '@/__generated__/useUserPrompts_query.graphql'
import { type useUserPromptsMutation } from '@/__generated__/useUserPromptsMutation.graphql'

export type UserPrompt = {
  promptId: string
  answer: string
}

export type UserPromptDetail = {
  promptId: string
  answer: string
  question: string
  isDeleted: boolean
}

type CommitAsyncFn = (data: UserPrompt[]) => Promise<useUserPrompts_query$data>

export const useUserPrompts = (
  queryRef?: useUserPrompts_query$key | null | undefined
): [UserPromptDetail[], CommitAsyncFn, boolean] => {
  const queryData = useFragment(
    graphql`
      fragment useUserPrompts_query on Query {
        currentUser {
          userPrompts(orderBy: [SEQUENCE_ASC]) {
            nodes {
              promptId
              answer
              prompt {
                question
                deletedAt
              }
            }
          }
        }
      }
    `,
    queryRef
  )
  const data = useMemo(
    () =>
      map(
        queryData?.currentUser?.userPrompts.nodes,
        (userPrompt) =>
          ({
            promptId: userPrompt!.promptId,
            answer: userPrompt!.answer,
            question: userPrompt!.prompt?.question ?? '',
            isDeleted: !!userPrompt!.prompt?.deletedAt
          }) as UserPromptDetail
      ),
    [queryData?.currentUser?.userPrompts.nodes]
  )
  const [commit, isPending] = useMutation<useUserPromptsMutation>(graphql`
    mutation useUserPromptsMutation($input: UpdateUserPromptsInput!) {
      updateUserPrompts(input: $input) {
        query {
          ...useUserPrompts_query
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (userPrompts: UserPrompt[]) =>
      new Promise((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              // @ts-ignore false negative
              resolve(response)
            }
          },
          variables: {
            input: {
              userPrompts
            }
          }
        })
      ),
    [commit]
  )

  return [data, commitAsync, isPending] as [UserPromptDetail[], CommitAsyncFn, boolean]
}
